import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import ErrorBoundary from "@/components/error-boundary";
import { useSearchParameters } from "@/components/signup/hooks";
import {
    ufoEventRedirectToProduct,
    ProductKey
} from "@/components/signup/utils";
import {
    ProvisioningStatus,
    TaskName,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src";
import { trackPerceivedProvisioningEvent } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/utils/analytics";

import {
    useCloudStatus,
    useCloudStatusRedirection,
    useCreateSampleProject,
    useTenantAwareStatsigClientUpdater
} from "../../../hooks";
import { createPollingFunction } from "../../../hooks/utils/use-cloud-status-utils";
import { JiraAnimatedProvisioningScreen } from "../../jira-animated-provisioning-screen/jira-animated-provisioning-screen";
import { JiraProductDiscoveryProvisioningScreen } from "../../jira-product-discovery-provisioning/jira-product-discovery-provisioning";
import ServiceCollectionProvisioningScreen from "../../service-collection-provisioning/service-collection-provisioning";

import { useProvisioningApi } from "./provisioning-api-provider";

export const Growth220OnboardingWizard = () => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const {
        cloudId,
        createCloudStatus,
        setCreateCloudStatus,
        startPerceivedProvisioningTimer,
        perceivedProvisioningStartTime
    } = useCloudStatus();
    const {
        setWaitingForProvisioning,
        addBlockingRedirectPromise,
        resolveBlockingRedirectPromises,
        deferRedirectView,
        setDeferRedirectView,
        createRedirectUrl
    } = useCloudStatusRedirection();
    const { products, signupId } = useSearchParameters();
    const { createSampleProject } = useCreateSampleProject();
    const { updatedStatsigClientWithCloudId } =
        useTenantAwareStatsigClientUpdater();
    const stargateClient = useBxpWebStargateClient();
    const { activateProductCrossflow, waitForProvisioning, projectKey } =
        useProvisioningApi();

    const activateProductCrossflowStarted = useRef(false);
    const provisioningEventFired = useRef(false);
    const createSampleProjectRequestInitialized = useRef(false);
    const provisionStarted = useRef(false);
    const [crossflowStarted, setCrossflowStarted] = useState(false);
    const [provisioningStep, setProvisioningStep] = useState<
        string | undefined
    >(undefined);
    const [provisioningStatus, setProvisioningStatus] = useState<
        ProvisioningStatus | undefined
    >(undefined);

    // There should be at most one product after filtering out Jira Software
    const product = products.filter(
        (product) => product !== ProductKey.JIRA_SOFTWARE
    )[0] as ProductKey;

    const handleOnError = useCallback(
        (error: Error) => {
            setCreateCloudStatus("error");
            createAnalyticsEvent({
                eventType: "operational",
                actionSubject: "growth220OnboardingWizardScreen",
                action: "failed",
                attributes: {
                    error: error.message
                }
            }).fire();
        },
        [createAnalyticsEvent, setCreateCloudStatus]
    );

    const pollForPrimaryProductCCP = useMemo(
        () =>
            createPollingFunction({
                checkFn: () =>
                    stargateClient.getLicenceInformationStatus({ cloudId }),
                callbacks: {
                    onSuccess: (_startTime, _result) => {
                        createAnalyticsEvent({
                            eventType: "operational",
                            action: "ready",
                            actionSubject:
                                "jsmGrowth220ProvisioningLicenseInformation",
                            attributes: {
                                productKey: product,
                                cloudId
                            }
                        }).fire();

                        const activateProductCrossflowPromise =
                            activateProductCrossflow(product);
                        addBlockingRedirectPromise(
                            activateProductCrossflowPromise
                        );
                        setCrossflowStarted(true);
                    },
                    onError: (_startTime, error) =>
                        handleOnError(error as Error)
                },
                isReady: (result) =>
                    Object.values(result.products ?? {}).some(
                        (prod) =>
                            !!prod.ccpTransactionAccountId &&
                            !!prod.ccpEntitlementId
                    )
            }),
        [
            activateProductCrossflow,
            addBlockingRedirectPromise,
            cloudId,
            createAnalyticsEvent,
            handleOnError,
            product,
            stargateClient
        ]
    );

    const onComplete = useCallback(
        async (projectKey: string) => {
            const productUrl = createRedirectUrl(projectKey);
            if (productUrl) {
                await resolveBlockingRedirectPromises();
                createAnalyticsEvent({
                    eventName: "redirectedToProduct",
                    eventComponent: "confirmationPage",
                    event: "redirected",
                    eventType: "ui",
                    action: "redirected",
                    attributes: {
                        isJsmGrowth220Enabled: true,
                        reactComponent: "JiraProvisioningWithOnboardingWizard",
                        productKey: product,
                        cloudId
                    }
                }).fire();

                // SLO "good event" for redirect into product
                ufoEventRedirectToProduct.success({
                    customData: {
                        analyticsEvent: "redirected",
                        redirectFrom: "confirmationPage",
                        reactComponent: "JiraProvisioningWithOnboardingWizard",
                        finalProvisioningScreen: "jira-onboarding-wizard"
                    }
                });

                window.location.assign(productUrl);
            }
        },
        [
            createRedirectUrl,
            resolveBlockingRedirectPromises,
            createAnalyticsEvent,
            product,
            cloudId
        ]
    );

    const pollForProvisioning = useMemo(
        () =>
            createPollingFunction({
                checkFn: () => waitForProvisioning(product),
                callbacks: {
                    onSuccess: (_startTime, response) => {
                        setProvisioningStep(response.step);
                        setProvisioningStatus(response.status);

                        if (response.context?.success) {
                            switch (response.status) {
                                case ProvisioningStatus.PROVISIONING_COMPLETED:
                                    onComplete(projectKey);
                                    break;
                                case ProvisioningStatus.PROVISIONING_ABORTED:
                                    switch (response.step) {
                                        case TaskName.ACTIVATE:
                                        case TaskName.WAIT_FOR_ACTIVATION:
                                        case TaskName.WAIT_FOR_JIRA:
                                            throw new Error(
                                                `Provisioning failed: ${response.status}`
                                            );
                                        case TaskName.CREATE_JIRA_PROJECT:
                                        case TaskName.ONBOARD_JPD_PROJECT:
                                            onComplete(projectKey);
                                            break;
                                        default:
                                            break;
                                    }
                                    break;
                                case ProvisioningStatus.PROVISIONING_FAILED:
                                default:
                                    createAnalyticsEvent({
                                        eventName:
                                            "jsmGrowth220ProvisioningFailed",
                                        eventType: "operational",
                                        action: "failed",
                                        actionSubject: "provisioning",
                                        attributes: {
                                            isJsmGrowth220Enabled: true,
                                            productKey: product,
                                            cloudId
                                        }
                                    }).fire();
                                    throw new Error(
                                        `Provisioning failed: ${response.status}`
                                    );
                            }
                        }
                    },
                    onError: (_startTime, error) =>
                        handleOnError(error as Error)
                },
                isReady: (result) => {
                    setProvisioningStep(result.step);
                    setProvisioningStatus(result.status);

                    return (
                        result.status !==
                            ProvisioningStatus.PROVISIONING_IN_PROGRESS &&
                        result.status !==
                            ProvisioningStatus.PROVISIONING_PROCESS_NOT_FOUND
                    );
                }
            }),
        [
            waitForProvisioning,
            product,
            onComplete,
            projectKey,
            handleOnError,
            createAnalyticsEvent,
            cloudId
        ]
    );

    const trackProvisioningEvent = useCallback(() => {
        if (!provisioningEventFired.current) {
            trackPerceivedProvisioningEvent({
                createAnalyticsEvent,
                productKey: product,
                finalProvisioningScreen:
                    "service-collection-provisioning-screen",
                startTimeSeconds: perceivedProvisioningStartTime,
                signupId
            });
            provisioningEventFired.current = true;
        }
    }, [
        createAnalyticsEvent,
        product,
        perceivedProvisioningStartTime,
        signupId
    ]);

    trackProvisioningEvent();

    useEffect(() => {
        if (
            updatedStatsigClientWithCloudId &&
            createCloudStatus === "success" &&
            createSampleProjectRequestInitialized.current === false
        ) {
            createSampleProjectRequestInitialized.current = true;
            const sampleProjectPromise = createSampleProject();
            addBlockingRedirectPromise(sampleProjectPromise);
        }
    }, [
        createCloudStatus,
        addBlockingRedirectPromise,
        createSampleProject,
        updatedStatsigClientWithCloudId
    ]);

    useEffect(() => {
        setWaitingForProvisioning(true);
    }, [setWaitingForProvisioning]);

    useEffect(() => {
        startPerceivedProvisioningTimer();
    }, [startPerceivedProvisioningTimer]);

    useEffect(() => {
        if (!deferRedirectView) {
            if (setDeferRedirectView) {
                setDeferRedirectView("service-collection-provisioning-screen");
            }
        }
    });

    useEffect(() => {
        if (
            updatedStatsigClientWithCloudId &&
            createCloudStatus === "success" &&
            provisionStarted.current === false
        ) {
            provisionStarted.current = true;
            pollForPrimaryProductCCP();
        }
    }, [
        createCloudStatus,
        pollForPrimaryProductCCP,
        product,
        updatedStatsigClientWithCloudId
    ]);

    useEffect(() => {
        if (
            activateProductCrossflowStarted.current === false &&
            crossflowStarted
        ) {
            activateProductCrossflowStarted.current = true;
            pollForProvisioning();
        }
    }, [
        pollForProvisioning,
        activateProductCrossflowStarted,
        crossflowStarted
    ]);

    return (
        <ErrorBoundary
            fallback={<JiraAnimatedProvisioningScreen />}
            onError={handleOnError}
        >
            {product === ProductKey.JIRA_PRODUCT_DISCOVERY ? (
                <JiraProductDiscoveryProvisioningScreen
                    provisioningStep={provisioningStep}
                    provisioningStatus={provisioningStatus}
                />
            ) : (
                <ServiceCollectionProvisioningScreen
                    provisioningStep={provisioningStep}
                    provisioningStatus={provisioningStatus}
                />
            )}
        </ErrorBoundary>
    );
};
